<template>
  <div class="carousel-container">
    <div 
      class="carousel-slide" 
      :style="{ transform: `translateX(-${currentSlide * 100}%)`, transition: `transform ${transitionSpeed}ms ease-in-out` }"
    >
      <div 
        v-for="item in carouselData" 
        :key="item.id || item.imageUrl" 
        class="carousel-item"
        :style="{ backgroundImage: `url(${item.imageUrl})` }"
      >
        <div class="carousel-overlay">
          <div class="carousel-content">
            <h2 v-if="item.title && item.title.trim()">{{ item.title }}</h2>
            <p v-if="item.description && item.description.trim()">{{ item.description }}</p>
            <button 
              v-if="item.buttonText" 
              class="carousel-button"
              @click="handleItemClick(item)"
            >
              {{ item.buttonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 轮播控制按钮 -->
    <button class="carousel-control prev" @click="prevSlide" aria-label="上一张">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
      </svg>
    </button>
    <button class="carousel-control next" @click="nextSlide" aria-label="下一张">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
      </svg>
    </button>
    
    <!-- 指示器 -->
    <div class="carousel-indicators">
      <button 
        v-for="(item, index) in carouselData" 
        :key="item.id || index"
        class="indicator"
        :class="{ active: currentSlide === index }"
        @click="goToSlide(index)"
        :aria-label="`转到幻灯片 ${index + 1}`"
      ></button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, withDefaults, defineProps, defineEmits, computed } from 'vue';
import { useI18n } from 'vue-i18n';

// 从接口获取的轮播图数据类型定义
interface ApiCarouselItem {
  id: number;
  imgUrl: string;
  imgUrlen: string;
  sort: number;
}

// 内部使用的轮播图数据类型定义
interface CarouselItem {
  imageUrl: string;
  title?: string;
  description?: string;
  buttonText?: string;
  link?: string;
  id?: number;
}

// Props 定义
interface Props {
  // 外部传入的轮播数据
  data?: CarouselItem[];
  // 自动播放间隔时间（毫秒）
  interval?: number;
  // 过渡动画速度（毫秒）
  transition?: number;
  // 是否自动播放
  autoplay?: boolean;
}

// 默认值
const props = withDefaults(defineProps<Props>(), {
  data: () => [],
  interval: 5000,
  transition: 500,
  autoplay: true
});

// Emits 定义
const emit = defineEmits<{
  // 图片点击事件
  (e: 'item-click', item: CarouselItem): void;
  // 幻灯片切换事件
  (e: 'slide-change', index: number): void;
}>();

// 状态管理
const currentSlide = ref(0);
const transitionSpeed = ref(props.transition);
let autoplayTimer: number | null = null;

// 静态轮播数据（预留接口位置后使用）
const defaultCarouselData: CarouselItem[] = [
  {
    imageUrl: 'https://evlife.com.hk/cdn/shop/files/2800x1000_wallbox_pulsar_1.png?v=1751281296&width=2600',
    title: '智能充电解决方案',
    description: '为您的电动车提供高效、安全的充电体验',
    buttonText: '了解更多'
  },
  {
    imageUrl: 'https://picsum.photos/id/1071/1600/900',
    title: 'EV 屋苑充电易',
    description: '专为住宅社区设计的充电解决方案',
    buttonText: '查看详情'
  },
  {
    imageUrl: 'https://evlife.com.hk/cdn/shop/files/ntt_charging_cable_01.png?v=1733816048&width=2600',
    title: '商用充电方案',
    description: '为商业场所提供定制化充电设施',
    buttonText: '联系我们'
  },
  {
    imageUrl: 'https://picsum.photos/id/1076/1600/900',
    title: '商用充电方案',
    description: '为商业场所提供定制化充电设施',
    buttonText: '联系我们'
  }
];

// 获取当前语言
const { locale } = useI18n();
const currentLanguage = ref(locale.value);

// 监听语言变化
watch(() => locale.value, (newLang) => {
  currentLanguage.value = newLang;
  // 语言变化时更新轮播图数据
  updateCarouselImages();
});

// 存储从接口获取的原始轮播图数据
const apiCarouselData = ref<ApiCarouselItem[]>([]);

// 使用外部数据或默认数据
const carouselData = ref<CarouselItem[]>(props.data.length > 0 ? props.data : defaultCarouselData);

// 根据当前语言更新轮播图图片
const updateCarouselImages = () => {
  if (apiCarouselData.value.length > 0) {
    carouselData.value = apiCarouselData.value.map(item => ({
      id: item.id,
      imageUrl: currentLanguage.value === 'en' ? item.imgUrlen.replace(/^\s*`|`\s*$/g, '') : item.imgUrl.replace(/^\s*`|`\s*$/g, ''),
      // 可以根据需要添加默认的标题和描述
      title: '',
      description: '',
      buttonText: '了解更多'
    }));
  }
};

// 从接口获取轮播图数据
const fetchCarouselData = async () => {
  try {
    const response = await fetch('/jeecg-boot/energycube/carousel/queryAllCarousel');
    const data = await response.json();
    if (data.code === 200 && data.result) {
      // 保存原始数据
      apiCarouselData.value = data.result;
      // 更新轮播图图片
      updateCarouselImages();
    }
    console.log("轮播图数据：", apiCarouselData.value);
  } catch (error) {
    console.error('获取轮播图数据失败:', error);
    // 接口调用失败时，使用默认数据
    if (carouselData.value.length === 0) {
      carouselData.value = defaultCarouselData;
    }
  }
};

// 监听数据变化
watch(() => props.data, (newData) => {
  if (newData && newData.length > 0) {
    carouselData.value = newData;
    currentSlide.value = 0; // 重置到第一张
  }
}, { deep: true });

// 监听间隔时间变化
watch(() => props.interval, (newInterval) => {
  if (props.autoplay) {
    stopAutoplay();
    startAutoplay(newInterval);
  }
});

// 监听自动播放状态变化
watch(() => props.autoplay, (newAutoplay) => {
  if (newAutoplay) {
    startAutoplay();
  } else {
    stopAutoplay();
  }
});

// 下一张
const nextSlide = () => {
  transitionSpeed.value = props.transition;
  currentSlide.value = (currentSlide.value + 1) % carouselData.value.length;
  emit('slide-change', currentSlide.value);
  // 手动切换后重新启动自动播放
  restartAutoplay();
};

// 上一张
const prevSlide = () => {
  transitionSpeed.value = props.transition;
  currentSlide.value = (currentSlide.value - 1 + carouselData.value.length) % carouselData.value.length;
  emit('slide-change', currentSlide.value);
  // 手动切换后重新启动自动播放
  restartAutoplay();
};

// 跳转到指定幻灯片
const goToSlide = (index: number) => {
  if (index !== currentSlide.value) {
    transitionSpeed.value = props.transition;
    currentSlide.value = index;
    emit('slide-change', currentSlide.value);
    // 手动切换后重新启动自动播放
    restartAutoplay();
  }
};

// 重新启动自动播放
const restartAutoplay = () => {
  stopAutoplay();
  if (props.autoplay && carouselData.value.length > 1) {
    setTimeout(() => {
      startAutoplay();
    }, props.interval);
  }
};

// 处理点击事件
const handleItemClick = (item: CarouselItem) => {
  emit('item-click', item);
};

// 开始自动播放
const startAutoplay = (interval?: number) => {
  stopAutoplay();
  autoplayTimer = window.setInterval(() => {
    nextSlide();
  }, interval || props.interval);
};

// 停止自动播放
const stopAutoplay = () => {
  if (autoplayTimer) {
    clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
};

// 生命周期钩子
onMounted(() => {
  // 组件挂载时获取轮播图数据
  fetchCarouselData();
  if (props.autoplay && carouselData.value.length > 1) {
    startAutoplay();
  }
});

onUnmounted(() => {
  stopAutoplay();
});
</script>

<style scoped lang="scss">
.carousel-container {
  position: relative;
  width: 100%;
  height: 800px;
  overflow: hidden;
  background-color: #f0f0f0;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    height: 300px;
  }

  @media (max-width: 480px) {
    height: 200px;
    border-radius: 0;
  }
}

.carousel-slide {
  display: flex;
  width: 100%;
  height: 100%;
}

.carousel-item {
  position: relative;
  min-width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &:hover {
    .carousel-overlay {
      background-color: rgba(0, 0, 0, 0.5);
    }
  }
}

.carousel-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3));
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    background: rgba(0, 0, 0, 0.5);
  }
}

.carousel-content {
  position: absolute;
  top: 50%;
  left: 10%;
  transform: translateY(-50%);
  color: white;
  max-width: 500px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    left: 5%;
    right: 5%;
    max-width: none;
    text-align: center;
  }

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0 0 1rem;
    animation: fadeInUp 0.8s ease-out;

    @media (max-width: 768px) {
      font-size: 1.8rem;
    }

    @media (max-width: 480px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    margin: 0 0 1.5rem;
    animation: fadeInUp 1s ease-out 0.2s both;

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }

  .carousel-button {
    background-color: #4CAF50;
    color: white;
    border: none;
    padding: 12px 24px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 30px;
    cursor: pointer;
    transition: all 0.3s ease;
    animation: fadeInUp 1.2s ease-out 0.4s both;

    &:hover {
      background-color: #45a049;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

.carousel-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.8);
  color: #333;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background-color: white;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &.prev {
    left: 20px;
  }

  &.next {
    right: 20px;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    
    &.prev {
      left: 10px;
    }
    
    &.next {
      right: 10px;
    }
  }
}

.carousel-indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 10;

  @media (max-width: 768px) {
    bottom: 15px;
    gap: 8px;
  }
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
    transform: scale(1.2);
  }

  &.active {
    background-color: white;
    width: 30px;
    border-radius: 6px;
  }

  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
    
    &.active {
      width: 25px;
    }
  }
}

// 动画效果
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 增强用户体验的细节
.carousel-item {
  // 添加背景图片加载动画
  opacity: 0;
  animation: fadeIn 0.5s ease-in-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>